"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { FieldLabel } from "@puckeditor/core";
import { getToken } from "@/lib/api";
import { publicSitePath } from "@/lib/website/public-path";
import { isPublicUploadUrl } from "@/lib/website/public-image";

type UploadFieldProps = {
  field?: { label?: string };
  name?: string;
  value?: string;
  onChange: (value: string) => void;
};

type GalleryAsset = {
  id: string;
  filename: string;
  name: string;
  folderId?: string;
  alt?: string;
};

export function WebsiteImageField({ field, name, value = "", onChange }: UploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File) {
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const token = getToken();
      const response = await fetch("/api/upload", { method: "POST", body: form, headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const payload = await response.json() as { filename?: string; error?: string };
      if (!response.ok || !payload.filename) throw new Error(payload.error ?? "No se pudo subir la imagen");
      onChange(payload.filename);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No se pudo subir la imagen");
    } finally { setUploading(false); }
  }

  return <FieldLabel label={field?.label ?? "Imagen"}>
    <div style={{ display: "grid", gap: 8 }}>
      {value ? <Image width={320} height={92} sizes="320px" unoptimized={value.startsWith("http") || isPublicUploadUrl(value.startsWith("/") ? value : `/api/uploads/${value}`)} src={value.startsWith("http") ? value : `/api/uploads/${value}`} alt="Vista previa" style={{ width: "100%", height: 92, borderRadius: 8, objectFit: "cover" }} /> : <div style={{ padding: 18, border: "1px dashed #98A2B3", borderRadius: 8, color: "#667085", textAlign: "center" }}>Sin imagen</div>}
      <input ref={inputRef} name={name} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" hidden onChange={(event) => { const file = event.currentTarget.files?.[0]; if (file) void upload(file); }} />
      <button type="button" disabled={uploading} onClick={() => inputRef.current?.click()} style={{ padding: "9px 12px", border: "1px solid #D0D5DD", borderRadius: 8, background: "#fff", cursor: "pointer" }}>{uploading ? "Subiendo..." : value ? "Cambiar imagen" : "Subir imagen"}</button>
      {value && <button type="button" onClick={() => onChange("")} style={{ padding: 0, border: 0, background: "transparent", color: "#D92D20", cursor: "pointer" }}>Eliminar imagen</button>}
      {error && <small style={{ color: "#D92D20" }}>{error}</small>}
    </div>
  </FieldLabel>;
}

export const websiteImageField = { type: "custom", render: WebsiteImageField } as const;

export function GalleryImageField({ field, name, value = "", onChange }: UploadFieldProps) {
  const [assets, setAssets] = useState<GalleryAsset[]>([]);
  const [folders, setFolders] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const token = getToken();
    fetch("/api/site-config", { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((response) => response.json() as Promise<{ config?: { galleryAssets?: GalleryAsset[]; galleryFolders?: Array<{ id: string; name: string }> } }>)
      .then((payload) => { if (!cancelled) { setAssets(payload.config?.galleryAssets ?? []); setFolders(payload.config?.galleryFolders ?? []); } })
      .catch(() => { if (!cancelled) setAssets([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const selectedAsset = assets.find((asset) => asset.filename === value);
  return <FieldLabel label={field?.label ?? "Imagen de Galería"}>
    <div style={{ display: "grid", gap: 8 }}>
      {value ? <Image width={320} height={92} sizes="320px" unoptimized={value.startsWith("http") || isPublicUploadUrl(value.startsWith("/") ? value : `/api/uploads/${value}`)} src={value.startsWith("http") ? value : `/api/uploads/${value}`} alt={selectedAsset?.alt || "Vista previa"} style={{ width: "100%", height: 92, borderRadius: 8, objectFit: "cover" }} /> : <div style={{ padding: 18, border: "1px dashed #98A2B3", borderRadius: 8, color: "#667085", textAlign: "center" }}>Sin imagen</div>}
      <select name={name} value={value} onChange={(event) => onChange(event.currentTarget.value)} style={{ width: "100%", padding: "9px 10px", border: "1px solid #D0D5DD", borderRadius: 8, background: "#fff" }}>
        <option value="">{loading ? "Cargando galería..." : "Selecciona una imagen"}</option>
        {value && !selectedAsset && <option value={value}>Imagen actual</option>}
        {assets.map((asset) => <option key={asset.id} value={asset.filename}>{folders.find((folder) => folder.id === asset.folderId)?.name ? `${folders.find((folder) => folder.id === asset.folderId)?.name} · ` : ""}{asset.name}</option>)}
      </select>
      <small style={{ color: "#667085" }}>{assets.length > 0 ? "Puedes cambiarla desde Galería." : "Sube imágenes desde la sección Galería."}</small>
      {value && <button type="button" onClick={() => onChange("")} style={{ padding: 0, border: 0, background: "transparent", color: "#D92D20", cursor: "pointer" }}>Eliminar imagen</button>}
    </div>
  </FieldLabel>;
}

export const galleryImageField = { type: "custom", render: GalleryImageField } as const;

type ColorFieldProps = {
  field?: { label?: string };
  name?: string;
  value?: string;
  onChange: (value: string) => void;
};

const COLOR_PRESETS = ["#1769E0", "#FF385C", "#101828", "#667085", "#F8FAFC", "#FFFFFF", "#12355B", "#F59E0B"];

function isHexColor(value: string) {
  return /^#[0-9A-Fa-f]{6}$/.test(value);
}

export function WebsiteColorField({ field, name, value = "#FFFFFF", onChange }: ColorFieldProps) {
  const [draft, setDraft] = useState(value);
  // Keep the local editor input synchronized when Puck changes the selected field externally.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setDraft(value), [value]);
  const safeColor = isHexColor(value) ? value : "#FFFFFF";
  function changeText(next: string) {
    const normalized = next.startsWith("#") ? next : `#${next}`;
    setDraft(normalized);
    if (isHexColor(normalized)) onChange(normalized.toUpperCase());
  }
  return <FieldLabel label={field?.label ?? "Color"}>
    <div style={{ display: "grid", gap: 9 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <input aria-label={`${field?.label ?? "Color"} selector`} name={name} type="color" value={safeColor} onChange={(event) => { setDraft(event.currentTarget.value.toUpperCase()); onChange(event.currentTarget.value.toUpperCase()); }} style={{ width: 48, height: 40, padding: 3, border: "1px solid #D0D5DD", borderRadius: 9, background: "#fff", cursor: "pointer" }} />
        <input aria-label={`${field?.label ?? "Color"} hexadecimal`} inputMode="text" maxLength={7} pattern="#[0-9A-Fa-f]{6}" value={draft} onChange={(event) => changeText(event.currentTarget.value)} placeholder="#FFFFFF" style={{ minWidth: 0, flex: 1, padding: "10px 11px", border: `1px solid ${draft && !isHexColor(draft) ? "#F04438" : "#D0D5DD"}`, borderRadius: 9, color: "#101828", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 }} />
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} aria-label="Paleta rápida" role="group">
        {COLOR_PRESETS.map((color) => <button key={color} aria-label={`Usar ${color}`} title={color} type="button" onClick={() => { setDraft(color); onChange(color); }} style={{ width: 24, height: 24, padding: 0, border: color === safeColor ? "2px solid #101828" : "1px solid #D0D5DD", borderRadius: "50%", background: color, cursor: "pointer" }} />)}
      </div>
      {draft && !isHexColor(draft) && <small style={{ color: "#D92D20" }}>Usa un color hexadecimal válido, por ejemplo #EEEEEE.</small>}
    </div>
  </FieldLabel>;
}

export const colorField = { type: "custom", render: WebsiteColorField } as const;

function websiteAssetUrl(value: string) {
  return value.startsWith("http") || value.startsWith("/") ? value : `/api/uploads/${value}`;
}

function setWebsiteMeta(attribute: "name" | "property", key: string, content?: string) {
  const selector = `meta[${attribute}="${key}"]`;
  const current = document.head.querySelector<HTMLMetaElement>(selector);
  if (!content) {
    current?.remove();
    return;
  }
  const element = current ?? document.head.appendChild(document.createElement("meta"));
  element.setAttribute(attribute, key);
  element.setAttribute("content", content);
}

function fallbackControlLabel(element: HTMLElement) {
  const text = element.textContent?.replace(/\s+/g, " ").trim() ?? "";
  if (text && /[A-Za-zÁÉÍÓÚÜÑáéíóúüñ0-9]/.test(text)) return text;
  if (text.includes("×") || text.includes("✕")) return "Cerrar";
  if (text.includes("‹") || text.includes("←")) return "Anterior";
  if (text.includes("›") || text.includes("→")) return "Siguiente";
  if (text.includes("+") || text.includes("＋")) return "Aumentar valor";
  if (text.includes("−") || text.includes("-") || text.includes("﹣")) return "Reducir valor";
  return "Acción";
}

function controlLabel(element: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) {
  const labelledBy = element.getAttribute("aria-labelledby");
  if (labelledBy || element.getAttribute("aria-label")) return;
  const visibleLabel = element.closest("label")?.textContent?.replace(/\s+/g, " ").trim();
  const placeholder = element.getAttribute("placeholder");
  const names: Record<string, string> = {
    name: "Nombre",
    email: "Correo electrónico",
    message: "Mensaje",
    checkin: "Fecha de entrada",
    checkout: "Fecha de salida",
    guests: "Número de huéspedes",
  };
  const label = visibleLabel || placeholder || names[element.name] || element.name;
  if (label) element.setAttribute("aria-label", label);
}

function linkLabel(link: HTMLAnchorElement) {
  // Un enlace sin href no es interactivo: añadirle aria-label provoca un
  // error de accesibilidad en Lighthouse/PageSpeed. El editor puede generar
  // enlaces vacíos mientras una agencia todavía está configurando la página.
  if (!link.hasAttribute("href")) {
    link.removeAttribute("aria-label");
    link.removeAttribute("aria-labelledby");
    return;
  }
  if (link.getAttribute("aria-label") || link.getAttribute("aria-labelledby")) return;
  const text = link.textContent?.replace(/\s+/g, " ").trim();
  if (text && /[A-Za-zÁÉÍÓÚÜÑáéíóúüñ0-9]/.test(text)) return;
  const imageAlt = link.querySelector("img")?.getAttribute("alt");
  link.setAttribute("aria-label", imageAlt ? `Ver ${imageAlt}` : "Abrir enlace");
}

export function WebsiteAccessibility() {
  const markerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const root = markerRef.current?.parentElement ?? document.body;
    const enhance = () => {
      root.querySelectorAll<HTMLButtonElement>("button").forEach((button) => {
        if (button.getAttribute("aria-label") || button.getAttribute("aria-labelledby")) return;
        button.setAttribute("aria-label", fallbackControlLabel(button));
      });
      root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("input, textarea, select").forEach(controlLabel);
      root.querySelectorAll<HTMLAnchorElement>("a").forEach(linkLabel);
      root.querySelectorAll<HTMLImageElement>("img").forEach((image) => {
        if (!image.hasAttribute("alt")) image.setAttribute("alt", "Imagen");
      });
      root.querySelectorAll<HTMLFormElement>("form").forEach((form) => {
        if (!form.getAttribute("aria-label") && !form.getAttribute("aria-labelledby")) form.setAttribute("aria-label", "Formulario");
      });
      root.querySelectorAll<HTMLElement>("nav").forEach((nav, index) => {
        if (!nav.getAttribute("aria-label")) nav.setAttribute("aria-label", index === 0 ? "Navegación principal" : "Navegación del pie");
      });
      root.querySelectorAll<HTMLElement>("section").forEach((section) => {
        if (!section.getAttribute("aria-label") && section.id === "reservar") section.setAttribute("aria-label", "Motor de reservas");
        if (!section.getAttribute("aria-label") && section.id === "contacto") section.setAttribute("aria-label", "Contacto");
        if (!section.getAttribute("aria-label")) {
          const heading = section.querySelector("h1, h2, h3")?.textContent?.replace(/\s+/g, " ").trim();
          if (heading) section.setAttribute("aria-label", heading);
        }
      });
    };

    enhance();
    const observer = new MutationObserver(enhance);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return <span ref={markerRef} aria-hidden="true" hidden />;
}

export function WebsiteSeo({ title, description, favicon, socialImage, themeColor }: { title: string; description: string; favicon?: string; socialImage?: string; themeColor?: string }) {
  useEffect(() => {
    document.title = title;
    setWebsiteMeta("name", "description", description);
    setWebsiteMeta("name", "theme-color", themeColor);
    setWebsiteMeta("property", "og:title", title);
    setWebsiteMeta("property", "og:description", description);
    setWebsiteMeta("property", "og:image", socialImage ? websiteAssetUrl(socialImage) : undefined);
    setWebsiteMeta("name", "twitter:card", socialImage ? "summary_large_image" : "summary");
    setWebsiteMeta("name", "twitter:title", title);
    setWebsiteMeta("name", "twitter:description", description);
    setWebsiteMeta("name", "twitter:image", socialImage ? websiteAssetUrl(socialImage) : undefined);
    let icon = document.head.querySelector<HTMLLinkElement>("link[data-vilatio-favicon]");
    if (!icon) {
      icon = document.head.appendChild(document.createElement("link"));
      icon.dataset.vilatioFavicon = "true";
      icon.rel = "icon";
    }
    icon.href = favicon ? websiteAssetUrl(favicon) : "/icon.svg";
  }, [description, favicon, socialImage, themeColor, title]);
  return <WebsiteAccessibility />;
}

export function WebsiteContactForm({ agencyId, title, subtitle, buttonLabel }: { agencyId?: string; title: string; subtitle: string; buttonLabel: string }) {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!agencyId) return;
    setStatus("sending");
    const form = new FormData(event.currentTarget);
    try {
      const response = await fetch(`/api/contact/${agencyId}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: form.get("name"), email: form.get("email"), message: form.get("message") }) });
      if (!response.ok) throw new Error();
      event.currentTarget.reset(); setStatus("success");
    } catch { setStatus("error"); }
  }
  return <section id="contacto" style={{ maxWidth: 1180, margin: "0 auto", padding: "90px clamp(24px, 5vw, 72px)" }}><div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 48, alignItems: "start" }}><div><span style={{ color: "#1769E0", fontSize: 12, fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase" }}>Contacto</span><h2 style={{ margin: "10px 0", color: "#101828", fontSize: 42, letterSpacing: "-.04em" }}>{title}</h2><p style={{ color: "#667085", fontSize: 17, lineHeight: 1.6 }}>{subtitle}</p></div><form aria-label="Formulario de contacto" onSubmit={submit} style={{ display: "grid", gap: 14, padding: 24, border: "1px solid #E4E7EC", borderRadius: 20, background: "#fff", boxShadow: "0 18px 50px rgba(16, 24, 40, .10)" }}><input aria-label="Nombre" name="name" required placeholder="Nombre" style={{ padding: 14, border: "1px solid #D0D5DD", borderRadius: 10 }} /><input aria-label="Correo electrónico" name="email" required placeholder="Email" type="email" style={{ padding: 14, border: "1px solid #D0D5DD", borderRadius: 10 }} /><textarea aria-label="Mensaje" name="message" required placeholder="¿En qué podemos ayudarte?" rows={5} style={{ padding: 14, border: "1px solid #D0D5DD", borderRadius: 10, resize: "vertical" }} /><button disabled={status === "sending"} type="submit" style={{ padding: 14, border: 0, borderRadius: 10, background: "#1769E0", color: "#fff", fontWeight: 800 }}>{status === "sending" ? "Enviando..." : buttonLabel}</button>{status === "success" && <small style={{ color: "#039855" }}>Mensaje enviado correctamente.</small>}{status === "error" && <small style={{ color: "#D92D20" }}>No se pudo enviar. Inténtalo de nuevo.</small>}</form></div></section>;
}

type BookingDate = "checkin" | "checkout";

function dateKey(date: Date) {
  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-");
}

function dateFromKey(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function formatBookingDate(value: string) {
  return new Intl.DateTimeFormat("es-ES", { day: "numeric", month: "short" }).format(dateFromKey(value)).replace(".", "");
}

function firstOfCurrentMonth() {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function calendarDays(month: Date) {
  const firstDay = (month.getDay() + 6) % 7;
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  return Array.from({ length: firstDay + daysInMonth }, (_, index) => index < firstDay ? null : new Date(month.getFullYear(), month.getMonth(), index - firstDay + 1));
}

export function WebsiteBookingWidget({ agencyId, domain, title, subtitle, buttonLabel }: { agencyId?: string; domain?: string; title: string; subtitle: string; buttonLabel: string }) {
  const widgetRef = useRef<HTMLDivElement>(null);
  const [today] = useState(dateKey(new Date()));
  const [checkin, setCheckin] = useState("");
  const [checkout, setCheckout] = useState("");
  const [guests, setGuests] = useState(2);
  const [open, setOpen] = useState<"dates" | "guests" | null>(null);
  const [selecting, setSelecting] = useState<BookingDate>("checkin");
  const [viewMonth, setViewMonth] = useState(firstOfCurrentMonth);
  const [error, setError] = useState("");

  useEffect(() => {
    function closeOnOutside(event: MouseEvent) {
      const calendar = widgetRef.current?.querySelector('button[aria-label="Mes anterior"]')?.parentElement?.parentElement?.parentElement;
      if (open === "dates" && calendar && !calendar.contains(event.target as Node)) setOpen(null);
      if (open === "guests" && widgetRef.current && !widgetRef.current.contains(event.target as Node)) setOpen(null);
    }
    document.addEventListener("mousedown", closeOnOutside);
    return () => document.removeEventListener("mousedown", closeOnOutside);
  }, [open]);

  function chooseDate(value: string) {
    if (value < today) return;
    if (selecting === "checkin") {
      setCheckin(value);
      setCheckout("");
      setSelecting("checkout");
      setError("");
      return;
    }
    if (!checkin || value <= checkin) {
      setCheckin(value);
      setCheckout("");
      setSelecting("checkout");
      setError("");
      return;
    }
    setCheckout(value);
    setOpen(null);
    setError("");
  }

  function openDatePicker(which: BookingDate) {
    setSelecting(which);
    setOpen("dates");
    if (which === "checkin" && checkin) setViewMonth(new Date(dateFromKey(checkin).getFullYear(), dateFromKey(checkin).getMonth(), 1));
    if (which === "checkout" && checkout) setViewMonth(new Date(dateFromKey(checkout).getFullYear(), dateFromKey(checkout).getMonth(), 1));
  }

  function submit(event: React.FormEvent<HTMLFormElement>) {
    if (!checkin || !checkout || checkout <= checkin || !agencyId) {
      event.preventDefault();
      setError(!agencyId ? "Publica la web para activar las reservas." : "Selecciona una entrada y una salida válidas.");
      setOpen("dates");
      return;
    }
    event.preventDefault();
    const url = new URL(publicSitePath(agencyId, "/buscar", domain), window.location.origin);
    url.searchParams.set("dateFrom", checkin);
    url.searchParams.set("dateTo", checkout);
    url.searchParams.set("guests", String(guests));
    window.location.assign(url.toString());
  }

  const monthLabel = new Intl.DateTimeFormat("es-ES", { month: "long", year: "numeric" }).format(viewMonth);
  const days = calendarDays(viewMonth);
  const fieldButtonStyle: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, width: "100%", minHeight: 58, padding: "10px 14px", border: "1px solid #D0D5DD", borderRadius: 14, background: "#fff", color: "#101828", textAlign: "left", cursor: "pointer" };

  return (
    <section id="reservar" style={{ maxWidth: 1180, margin: "0 auto", padding: "76px clamp(24px, 5vw, 72px)" }}>
      <div ref={widgetRef} style={{ position: "relative", padding: "30px clamp(20px, 4vw, 42px) 34px", border: "1px solid #D9E4F2", borderRadius: 26, background: "linear-gradient(135deg, #F6FAFF, #FFFFFF)", boxShadow: "0 22px 60px rgba(16, 24, 40, .12)" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "end", justifyContent: "space-between", gap: 24, marginBottom: 24 }}>
          <div><span style={{ color: "#1769E0", fontSize: 12, fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase" }}>Reserva directa</span><h2 style={{ margin: "8px 0 6px", color: "#101828", fontSize: "clamp(28px, 4vw, 38px)", letterSpacing: "-.04em" }}>{title}</h2><p style={{ margin: 0, color: "#667085", fontSize: 16 }}>{subtitle}</p></div>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7, color: "#039855", fontSize: 13, fontWeight: 800 }}><span style={{ display: "grid", placeItems: "center", width: 22, height: 22, borderRadius: "50%", background: "#D1FADF" }}>✓</span>Mejor tarifa garantizada</span>
        </div>
        <form action={agencyId ? publicSitePath(agencyId, "/buscar", domain) : undefined} method="get" onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12, alignItems: "end" }}>
          <div style={{ position: "relative" }}><span style={{ display: "block", marginBottom: 7, color: "#667085", fontSize: 12, fontWeight: 800 }}>Entrada</span><button type="button" aria-expanded={open === "dates"} onClick={() => openDatePicker("checkin")} style={fieldButtonStyle}><span><strong style={{ display: "block", fontSize: 14 }}>{checkin ? formatBookingDate(checkin) : "Añadir fecha"}</strong><small style={{ display: "block", marginTop: 3, color: "#98A2B3" }}>¿Cuándo llegas?</small></span><span aria-hidden="true" style={{ fontSize: 21 }}>▣</span></button></div>
          <div style={{ position: "relative" }}><span style={{ display: "block", marginBottom: 7, color: "#667085", fontSize: 12, fontWeight: 800 }}>Salida</span><button type="button" aria-expanded={open === "dates"} onClick={() => openDatePicker("checkout")} style={fieldButtonStyle}><span><strong style={{ display: "block", fontSize: 14 }}>{checkout ? formatBookingDate(checkout) : "Añadir fecha"}</strong><small style={{ display: "block", marginTop: 3, color: "#98A2B3" }}>¿Cuándo te vas?</small></span><span aria-hidden="true" style={{ fontSize: 21 }}>▣</span></button></div>
          <div style={{ position: "relative" }}><span style={{ display: "block", marginBottom: 7, color: "#667085", fontSize: 12, fontWeight: 800 }}>Huéspedes</span><button type="button" aria-expanded={open === "guests"} onClick={() => setOpen(open === "guests" ? null : "guests")} style={fieldButtonStyle}><span><strong style={{ display: "block", fontSize: 14 }}>{guests} {guests === 1 ? "huésped" : "huéspedes"}</strong><small style={{ display: "block", marginTop: 3, color: "#98A2B3" }}>¿Cuántos viajáis?</small></span><span aria-hidden="true" style={{ fontSize: 21 }}>♙</span></button>{open === "guests" && <div style={{ position: "absolute", zIndex: 10, top: "calc(100% + 10px)", right: 0, width: 260, padding: 18, border: "1px solid #E4E7EC", borderRadius: 18, background: "#fff", boxShadow: "0 18px 45px rgba(16, 24, 40, .18)" }}><div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 18 }}><div><strong style={{ display: "block", color: "#101828" }}>Huéspedes</strong><small style={{ color: "#667085" }}>Máximo 16 personas</small></div><div style={{ display: "flex", alignItems: "center", gap: 10 }}><button type="button" aria-label="Restar huésped" disabled={guests <= 1} onClick={() => setGuests((value) => Math.max(1, value - 1))} style={{ width: 34, height: 34, border: "1px solid #D0D5DD", borderRadius: "50%", background: "#fff", color: "#1769E0", fontSize: 20, cursor: guests <= 1 ? "not-allowed" : "pointer", opacity: guests <= 1 ? .45 : 1 }}>−</button><strong style={{ minWidth: 18, color: "#101828", textAlign: "center" }}>{guests}</strong><button type="button" aria-label="Sumar huésped" disabled={guests >= 16} onClick={() => setGuests((value) => Math.min(16, value + 1))} style={{ width: 34, height: 34, border: "1px solid #D0D5DD", borderRadius: "50%", background: "#fff", color: "#1769E0", fontSize: 20, cursor: guests >= 16 ? "not-allowed" : "pointer", opacity: guests >= 16 ? .45 : 1 }}>+</button></div></div></div>}</div>
          <input type="hidden" name="checkin" value={checkin} /><input type="hidden" name="checkout" value={checkout} /><input type="hidden" name="guests" value={guests} /><button type="submit" style={{ minHeight: 58, border: 0, borderRadius: 14, background: "#1769E0", color: "#fff", fontWeight: 850, cursor: "pointer", boxShadow: "0 12px 24px rgba(23, 105, 224, .24)" }}>{buttonLabel} <span aria-hidden="true">→</span></button>
        </form>
        {open === "dates" && <div style={{ position: "absolute", zIndex: 12, top: "calc(100% - 10px)", left: 20, width: "min(368px, calc(100% - 40px))", padding: 20, border: "1px solid #E4E7EC", borderRadius: 20, background: "#fff", boxShadow: "0 22px 55px rgba(16, 24, 40, .2)" }}><div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 16 }}><div><strong style={{ display: "block", color: "#101828", textTransform: "capitalize" }}>{monthLabel}</strong><small style={{ color: "#667085" }}>Selecciona {selecting === "checkin" ? "la entrada" : "la salida"}</small></div><div style={{ display: "flex", gap: 6 }}><button type="button" aria-label="Mes anterior" disabled={viewMonth <= firstOfCurrentMonth()} onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))} style={{ width: 32, height: 32, border: "1px solid #D0D5DD", borderRadius: "50%", background: "#fff", cursor: "pointer", opacity: viewMonth <= firstOfCurrentMonth() ? .4 : 1 }}>‹</button><button type="button" aria-label="Mes siguiente" onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))} style={{ width: 32, height: 32, border: "1px solid #D0D5DD", borderRadius: "50%", background: "#fff", cursor: "pointer" }}>›</button></div></div><div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 6 }}>{["lu", "ma", "mi", "ju", "vi", "sá", "do"].map((day) => <span key={day} style={{ color: "#98A2B3", fontSize: 11, fontWeight: 800, textAlign: "center", textTransform: "uppercase" }}>{day}</span>)}</div><div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>{days.map((day, index) => { if (!day) return <span key={`empty-${index}`} />; const value = dateKey(day); const disabled = value < today || (selecting === "checkout" && !!checkin && value <= checkin); const selected = value === checkin || value === checkout; const inRange = !!checkin && !!checkout && value > checkin && value < checkout; return <button key={value} type="button" disabled={disabled} onClick={() => chooseDate(value)} aria-label={formatBookingDate(value)} style={{ minHeight: 38, border: 0, borderRadius: 10, background: selected ? "#1769E0" : inRange ? "#E8F1FF" : "transparent", color: selected ? "#fff" : disabled ? "#D0D5DD" : "#101828", fontWeight: selected || value === today ? 800 : 500, cursor: disabled ? "not-allowed" : "pointer" }}>{day.getDate()}</button>; })}</div></div>}
        {error && <p role="alert" style={{ margin: "14px 0 0", color: "#D92E20", fontSize: 13, fontWeight: 700 }}>{error}</p>}
      </div>
    </section>
  );
}
